import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { useToast } from "../toast";

export interface ConnectorFormData {
  connector_number: number | "";
  connector_type: string;
  max_power_kw: number | "";
}

interface Props {
  open: boolean;
  title: string;
  initial?: ConnectorFormData | null;
  onSave: (data: ConnectorFormData) => Promise<void>;
  onClose: () => void;
}

const empty: ConnectorFormData = { connector_number: "", connector_type: "", max_power_kw: "" };

export default function ConnectorDialog({ open, title, initial, onSave, onClose }: Props) {
  const [form, setForm] = useState<ConnectorFormData>(empty);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (open) setForm(initial ?? empty);
  }, [open, initial]);

  const handleSave = async () => {
    if (!form.connector_type || form.connector_number === "") return;
    setSaving(true);
    try {
      await onSave(form);
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Connector #"
            type="number"
            required
            inputProps={{ min: 1, step: 1 }}
            value={form.connector_number}
            onChange={(e) =>
              setForm({ ...form, connector_number: e.target.value === "" ? "" : parseInt(e.target.value) })
            }
          />
          <TextField
            label="Connector Type"
            required
            placeholder="e.g. Type2, CCS2, CHAdeMO"
            value={form.connector_type}
            onChange={(e) => setForm({ ...form, connector_type: e.target.value })}
          />
          <TextField
            label="Max Power (kW)"
            type="number"
            inputProps={{ step: 0.1, min: 0 }}
            value={form.max_power_kw}
            onChange={(e) =>
              setForm({ ...form, max_power_kw: e.target.value === "" ? "" : parseFloat(e.target.value) })
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || !form.connector_type || form.connector_number === ""}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

